import classNames from 'classnames';
import React, {useCallback, useRef, useState} from 'react';

import * as ExportConstants from 'constants/ExportConstants';
import {Snapshot} from 'notebook/reviews/ReviewConstants';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {RelativeTime} from 'ui-library/Times';
import APIUtil from 'util/APIUtil';
import {capitalize, typeToName} from 'util/NameUtil';

import postReviewActionServerUpdate from './postReviewActionServerUpdate';
import {retryReviewSnapshot} from './ReviewDAO';

function SnapshotText({snapshot, apiType}: {snapshot: Snapshot; apiType: string}): JSX.Element {
  const {STATUS} = ExportConstants;
  const fileType = capitalize(typeToName(apiType));
  switch (snapshot.status) {
    case STATUS.PENDING:
    case STATUS.RUNNING:
      return (
        <span>
          {fileType} snapshot started <RelativeTime datetime={snapshot.createdAt} />.
        </span>
      );
    case STATUS.FAILED:
      return <span>{fileType} snapshot failed.</span>;
    case STATUS.SUCCEEDED:
      return (
        <span>
          {fileType} snapshot succeeded <RelativeTime datetime={snapshot.createdAt} />.
        </span>
      );
    default:
      throw new Error(`Unexpected snapshot status ${snapshot.status}`);
  }
}

const STATUS_TO_ICON: Record<string, BenchlingIconName> = {
  [ExportConstants.STATUS.PENDING]: 'request-in-progress',
  [ExportConstants.STATUS.RUNNING]: 'request-in-progress',
  [ExportConstants.STATUS.FAILED]: 'request-failed',
  [ExportConstants.STATUS.SUCCEEDED]: 'download',
};

function SnapshotIcon({snapshot, className}: {snapshot: Snapshot; className?: string}): JSX.Element {
  const icon = STATUS_TO_ICON[snapshot.status];
  return <BenchlingIcon icon={icon} className={className} />;
}

export default function SnapshotInfo({
  snapshot,
  apiType,
}: {
  snapshot: Snapshot;
  apiType: string;
}): JSX.Element {
  const snapshotMessage = (
    <div className="u-small-spacing-top">
      <SnapshotIcon snapshot={snapshot} className="icon u-small-spacing-right text-center" />
      <SnapshotText apiType={apiType} snapshot={snapshot} />
    </div>
  );
  if (snapshot.url) {
    return <a href={snapshot.url}>{snapshotMessage}</a>;
  }
  return snapshotMessage;
}

/**
 * Surface a retry action for failed snapshot, with similar interface to HistoryItemIcon + HistoryItemSummary.
 *
 * `refetch` will be invoked after the snapshot retry completes.
 */
export function LastSnapshotFailedIconAndSummary({
  fileId,
  reviewId,
  refetch,
}: {
  fileId: string;
  reviewId: string;
  refetch?: () => void;
}): JSX.Element {
  // Capture when the user is attempting the retry + we are polling to give feedback.
  const [isRetrying, setIsRetrying] = useState(false);

  // In case the component re-renders with a new refetch function, make sure we use the latest one.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const handleClick = useCallback(async () => {
    setIsRetrying(true);
    const {review, filePermissions, snapshotTask} = await retryReviewSnapshot(fileId, reviewId);
    postReviewActionServerUpdate(fileId, review, filePermissions, snapshotTask, {
      refetch: () => {
        refetchRef.current?.();
        setIsRetrying(false);
      },
    });
  }, [fileId, reviewId]);

  return (
    <>
      <BenchlingIcon
        icon={isRetrying ? 'refresh' : 'request-in-progress'}
        className={classNames('icon u-small-spacing-right text-center', {'glyphicon-spin': isRetrying})}
      />
      <span>
        Latest {typeToName(APIUtil.apiIdToType(fileId))} snapshot failed.{' '}
        {!isRetrying ? <a onClick={handleClick}>Retry?</a> : 'Retrying...'}
      </span>
    </>
  );
}
